import React from 'react';
import { makeStyles, Typography, Grid, Button, Theme, Hidden, Switch, Tooltip } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useKrispToggle } from '../../../hooks/useKrispToggle/useKrispToggle';
import SmallCheckIcon from '../../../icons/SmallCheckIcon';
import InfoIconOutlined from '../../../icons/InfoIconOutlined';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '450px',
    justifyContent: 'space-between',
  },
  gutterBottom: {
    marginBottom: '1.5em',
    color: '#2c3e50',
    fontWeight: 'bold',
  },
  marginTop: {
    marginTop: '1em',
  },
  deviceButton: {
    width: '100%',
    border: '2px solid #e1e8ed',
    borderRadius: '8px',
    margin: '0.75em 0',
    padding: '12px 16px',
    fontSize: '0.95rem',
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      borderColor: '#3498db',
      backgroundColor: '#f8fbff',
    },
    '&:disabled': {
      borderColor: '#e1e8ed',
      backgroundColor: '#f5f7fa',
      color: '#95a5a6',
    },
  },
  localPreviewContainer: {
    paddingRight: '2em',
    marginBottom: '1.5em',
    [theme.breakpoints.down('sm')]: {
      padding: '0',
      marginBottom: '1em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginTop: 'auto',
    paddingTop: '1em',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      gap: '12px',
      justifyContent: 'stretch',
      '& button': {
        width: '100%',
      },
    },
  },
  joinButton: {
    backgroundColor: '#2980b9',
    color: 'white',
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: '8px',
    minWidth: '140px',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#3498db',
    },
    '&:disabled': {
      backgroundColor: '#bdc3c7',
      color: '#95a5a6',
    },
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: '#2c3e50',
    border: '2px solid #e1e8ed',
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: 500,
    textTransform: 'none',
    borderRadius: '8px',
    minWidth: '140px',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: '#95a5a6',
      backgroundColor: '#f8f9fa',
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em 0 1em',
      gap: '8px',
    },
  },
  mobileButton: {
    padding: '10px 16px',
    margin: 0,
    fontSize: '0.9rem',
    textTransform: 'none',
    borderRadius: '6px',
    flex: 1,
  },
  toolTipContainer: {
    display: 'flex',
    alignItems: 'center',
    '& div': {
      display: 'flex',
      alignItems: 'center',
    },
    '& svg': {
      marginLeft: '0.3em',
      color: '#7f8c8d',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    '& .MuiCircularProgress-root': {
      color: '#3498db',
      marginBottom: '16px',
    },
  },
  loadingText: {
    color: '#2c3e50',
    fontWeight: 500,
  },
  settingsSection: {
    padding: '16px 0',
    borderTop: '1px solid #e1e8ed',
    marginTop: '16px',
  },
}));

interface DeviceSelectionScreenProps {
  name: string;
  roomName: string;
  setStep: (step: Steps) => void;
}

export default function DeviceSelectionScreen({ name, roomName, setStep }: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const { getToken, isFetching, isKrispEnabled, isKrispInstalled } = useAppState();
  const { connect: chatConnect } = useChatContext();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const { toggleKrisp } = useKrispToggle();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting;

  const handleJoin = () => {
    getToken(name, roomName).then(({ token }) => {
      videoConnect(token);
      process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true' && chatConnect(token);
    });
  };

  const history = useHistory();

  const handleCancelClick = () => {
    history.replace('/');
    window.location.reload();
  };

  if (isFetching || isConnecting) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress variant="indeterminate" size={48} />
        <Typography variant="body1" className={classes.loadingText}>
          Joining Meeting...
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div>
        <Typography variant="h4" className={classes.gutterBottom}>
          Join {roomName}
        </Typography>

        <Grid container justifyContent="center" spacing={3}>
          <Grid item md={7} sm={12} xs={12}>
            <div className={classes.localPreviewContainer}>
              <LocalVideoPreview identity={name} />
            </div>
            <div className={classes.mobileButtonBar}>
              <Hidden mdUp>
                <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
                <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} />
                <SettingsMenu mobileButtonClass={classes.mobileButton} />
              </Hidden>
            </div>
          </Grid>
          <Grid item md={5} sm={12} xs={12}>
            <Grid container direction="column" justifyContent="space-between" style={{ alignItems: 'normal' }}>
              <div>
                <Hidden smDown>
                  <ToggleAudioButton className={classes.deviceButton} disabled={disableButtons} />
                  <ToggleVideoButton className={classes.deviceButton} disabled={disableButtons} />
                </Hidden>
              </div>
            </Grid>
          </Grid>
        </Grid>

        {isKrispInstalled && (
          <div className={classes.settingsSection}>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <div className={classes.toolTipContainer}>
                <Typography variant="subtitle2" style={{ color: '#2c3e50', fontWeight: 500 }}>
                  Noise Cancellation
                </Typography>
                <Tooltip
                  title="Suppress background noise from your microphone"
                  interactive
                  leaveDelay={250}
                  leaveTouchDelay={15000}
                  enterTouchDelay={0}
                >
                  <div>
                    <InfoIconOutlined />
                  </div>
                </Tooltip>
              </div>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!isKrispEnabled}
                    checkedIcon={<SmallCheckIcon />}
                    disableRipple={true}
                    onClick={toggleKrisp}
                  />
                }
                label={isKrispEnabled ? 'Enabled' : 'Disabled'}
                style={{ marginRight: 0 }}
                disabled={isKrispEnabled && isAcquiringLocalTracks}
              />
            </Grid>
          </div>
        )}
      </div>

      <Grid container direction="row" alignItems="center" spacing={2}>
        <Hidden smDown>
          <Grid item md={7} sm={6} xs={12}>
            <SettingsMenu mobileButtonClass={classes.mobileButton} />
          </Grid>
        </Hidden>

        <Grid item md={5} sm={6} xs={12}>
          <div className={classes.joinButtons}>
            <Button className={classes.cancelButton} onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button className={classes.joinButton} data-cy-join-now onClick={handleJoin} disabled={disableButtons}>
              Join Now
            </Button>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}
